/**
 * Tab Store Implementation
 *
 * React Router DOM 환경에서 멀티탭 네비게이션을 위한 Zustand store 구현
 * localStorage를 통한 영속성 지원으로 브라우저 새로고침 시에도 탭 상태 유지
 *
 * 주요 기능:
 * - 탭 목록 관리 (추가, 제거, 업데이트)
 * - 현재 활성 탭 추적
 * - URL search parameters 관리
 * - localStorage를 통한 탭 상태 영속화
 */
import { SEARCH_PARAM_KEY_BRANCH_ID, SEARCH_PARAM_KEY_DRAWER } from '@/consts/search-param-keys';
import { createStore } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * 개별 탭 정보를 나타내는 인터페이스
 *
 * @interface Tab
 * @property {string} key - 탭의 고유 식별자, 일반적으로 라우트 경로 (예: '/dashboard', '/users')
 * @property {string} [search] - 탭에 저장된 URL search parameters (선택적)
 */
export interface Tab {
  key: string;
  search?: string;
}

/**
 * 탭 상태를 나타내는 인터페이스
 *
 * @interface TabState
 * @property {string | undefined} currentTabKey - 현재 활성화된 탭의 키, undefined면 활성 탭 없음
 * @property {Tab[]} tabs - 열린 탭들의 배열, 순서는 탭 표시 순서와 동일
 */
interface TabState {
  currentTabKey: string | undefined;
  tabs: Tab[];
}

/**
 * 탭 상태를 조작하는 액션들을 정의하는 인터페이스
 *
 * @interface TabActions
 */
interface TabActions {
  /** 현재 store 상태를 반환하는 getter 함수 */
  getTabStore: () => TabState;

  /** 현재 활성 탭을 설정하는 함수 */
  setCurrentTabKey: (tabKey: string) => void;

  /** 탭 배열을 직접 설정하거나 업데이트 함수를 통해 변경하는 함수 */
  setTabs: (update: Tab[] | ((tabs: Tab[]) => Tab[])) => void;

  /** 새로운 탭을 추가하는 함수 (중복 키 방지) */
  addTab: (newTab: Tab) => void;

  /** 지정된 키의 탭을 제거하고 제거된 인덱스를 반환하는 함수 */
  removeTab: (tabKey: string) => number;

  /** 기존 탭을 업데이트하는 함수 (search parameters 필터링 포함) */
  updateTab: (tabKey: string, update: Tab | ((tab: Tab) => Tab)) => void;
}

/** TabState와 TabActions를 결합한 완전한 탭 store 타입 */
export type TabStore = TabState & TabActions;

/** 탭 store의 초기 상태 */
const defaultTabState: TabState = {
  currentTabKey: undefined, // 초기에는 활성 탭 없음
  tabs: [], // 빈 탭 목록으로 시작
};

/**
 * 탭 store 인스턴스를 생성하는 팩토리 함수
 *
 * Zustand의 persist 미들웨어를 사용하여 localStorage에 탭 상태를 저장
 * 브라우저 새로고침 시에도 탭 목록이 유지됨 (현재 활성 탭은 제외)
 *
 * @param {TabState} [initState=defaultTabState] - 초기 상태 (선택적)
 * @returns {StoreApi<TabStore>} Zustand store 인스턴스
 */
export const createTabStore = (initState: TabState = defaultTabState) => {
  return createStore<TabStore>()(
    persist(
      (set, get) => ({
        ...initState,

        // Store 상태 전체를 반환하는 getter
        getTabStore: get,

        // 현재 활성 탭 설정
        setCurrentTabKey: (tabKey) => {
          set({ currentTabKey: tabKey });
        },

        // 탭 배열 직접 설정 또는 함수형 업데이트
        setTabs: (update) => {
          set((state) => ({ tabs: typeof update === 'function' ? update(state.tabs) : update }));
        },

        // 새 탭 추가 (중복 키 방지)
        addTab: (newTab) => {
          set((state) => ({
            tabs: state.tabs.some((tab) => tab.key === newTab.key)
              ? state.tabs // 이미 존재하면 변경 없음
              : [...state.tabs, newTab], // 새 탭 추가
          }));
        },

        // 탭 제거 및 제거된 인덱스 반환
        removeTab: (tabKey) => {
          const index = get().tabs.findIndex((tab) => tab.key === tabKey);
          if (index !== -1) {
            set((state) => ({
              tabs: [...state.tabs.slice(0, index), ...state.tabs.slice(index + 1)],
            }));
          }
          return index; // -1이면 탭을 찾지 못함
        },

        // 기존 탭 업데이트 (search parameters 필터링 포함)
        updateTab: (tabKey, update) => {
          set((state) => {
            const index = state.tabs.findIndex((tab) => tab.key === tabKey);
            if (index !== -1) {
              const newTab = typeof update === 'function' ? update(state.tabs[index]) : update;

              // URL search parameters에서 탭에서 관리하지 않는 파라미터들 제거
              const urlSearchParams = new URLSearchParams(newTab.search ?? '');
              // 지점 정보는 탭에서 관리하지 않음
              urlSearchParams.delete(SEARCH_PARAM_KEY_BRANCH_ID);
              // 드로어 정보는 탭에서 관리하지 않음
              urlSearchParams.delete(SEARCH_PARAM_KEY_DRAWER);
              newTab.search = urlSearchParams.toString();

              return {
                tabs: [...state.tabs.slice(0, index), newTab, ...state.tabs.slice(index + 1)],
              };
            }
            return { tabs: state.tabs }; // 탭을 찾지 못한 경우 변경 없음
          });
        },
      }),
      {
        name: TAB_STORE_LOCALSTORAGE_KEY,
        // 탭 목록만 localStorage에 저장 (현재 활성 탭은 브라우저 새로고침 시 초기화)
        partialize: ({ tabs }) => ({ tabs }),
      },
    ),
  );
};

/** localStorage에 탭 상태를 저장할 때 사용하는 키 */
const TAB_STORE_LOCALSTORAGE_KEY = 'tab';
